import { useMemo, useState } from "react";
import { ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Bundle } from "@/pages/bundles/types/bundle";

// Detail line rendered under a bundle name: identically named bundles differ
// by type/price/product, so surface those. Region is deliberately absent —
// every consumer scopes the list by region before this picker (create-pack
// cascade) or shows same-region bundles, so it would only repeat itself.
export function getBundleDetailLine(bundle: Bundle): string {
  return [bundle.type, bundle.amount, (bundle as any).product?.name]
    .filter(Boolean)
    .join(" · ");
}

interface BundleComboboxProps {
  bundles: Bundle[];
  value?: string;
  onSelect: (bundleId: string) => void;
  placeholder?: string;
  // Shown when `value` isn't in `bundles` (e.g. a usage pointing at a retired
  // bundle that the active list no longer contains).
  fallbackLabel?: string;
  triggerClassName?: string;
  disabled?: boolean;
  // For pick-and-add consumers (multi-select kept as external chips, e.g. the
  // coupon form): renders a "Select All" row that hands back the ids of every
  // bundle currently VISIBLE — i.e. after the search filter is applied.
  onSelectAll?: (visibleBundleIds: string[]) => void;
  // Overrides the detail line rendered under each option — display only.
  // The search haystack covers what this displays PLUS the default
  // type/amount/product line, so overriding never narrows what's searchable.
  getDetail?: (bundle: Bundle) => string;
  // Shown when the (unfiltered) bundle list is empty, e.g. to distinguish
  // "loading" from "no bundles in this region".
  emptyMessage?: string;
}

// Searchable bundle picker: filters on everything the option displays —
// the name plus the detail line — and always on the default
// type/amount/product line, even when `getDetail` overrides the display.
export function BundleCombobox({
  bundles,
  value,
  onSelect,
  placeholder = "Select a bundle",
  fallbackLabel,
  triggerClassName,
  disabled,
  onSelectAll,
  getDetail,
  emptyMessage = "No bundles found",
}: BundleComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const detail = getDetail ?? getBundleDetailLine;

  // One lowercase haystack per bundle so each keystroke is a single
  // includes() per row, not repeated field reads + toLowerCase calls.
  const searchable = useMemo(
    () =>
      bundles.map((bundle) => ({
        bundle,
        haystack: [
          bundle.name,
          detail(bundle),
          // With a custom detail line, still index the default one so the
          // override can't drop type/product-name search.
          getDetail ? getBundleDetailLine(bundle) : null,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase(),
      })),
    [bundles, detail, getDetail],
  );

  const query = search.trim().toLowerCase();
  const filtered = query
    ? searchable.filter((s) => s.haystack.includes(query))
    : searchable;

  const selectedName = value
    ? (bundles.find((b) => b.id === value)?.name ?? fallbackLabel)
    : undefined;

  const handlePick = (bundleId: string) => {
    onSelect(bundleId);
    setOpen(false);
    setSearch("");
  };

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) setSearch("");
      }}
      // The pickers render inside scroll-locked containers (Vaul drawer, the
      // create-pack Dialog). A non-modal popover portals to document.body,
      // where the lock swallows wheel/touch events — the list won't scroll.
      // modal opts the popover content out of the parent's scroll lock.
      modal
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={`justify-between font-normal ${triggerClassName ?? ""}`}
        >
          <span
            className={`truncate min-w-0 ${!selectedName ? "text-muted-foreground" : ""}`}
          >
            {selectedName ?? placeholder}
          </span>
          <ChevronsUpDown className="h-3 w-3 opacity-50 flex-shrink-0 ml-1" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[340px] p-2">
        <Input
          placeholder="Search name, type, product..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-8 text-xs mb-2"
          autoFocus
        />
        <div className="max-h-64 overflow-y-auto space-y-0.5">
          {filtered.length === 0 && (
            <div className="text-xs text-gray-500 px-2 py-1.5">
              {bundles.length === 0 ? emptyMessage : "No bundles found"}
            </div>
          )}
          {onSelectAll && filtered.length > 0 && (
            <button
              type="button"
              onClick={() => {
                onSelectAll(filtered.map(({ bundle }) => bundle.id));
                // Stay open so per-region batches can be chained (search
                // "ae" → Select All → search "in" → Select All); only the
                // query resets, since everything it matched is now added.
                setSearch("");
              }}
              className="w-full text-left px-2 py-1.5 rounded hover:bg-gray-100"
            >
              <div className="text-xs font-medium">
                Select All{query ? " (filtered)" : ""}
              </div>
              <div className="text-xs text-muted-foreground">
                Add all {filtered.length} bundle
                {filtered.length === 1 ? "" : "s"} shown below
              </div>
            </button>
          )}
          {filtered.map(({ bundle }) => (
            <button
              key={bundle.id}
              type="button"
              onClick={() => handlePick(bundle.id)}
              className={`w-full text-left px-2 py-1.5 rounded hover:bg-gray-100 ${
                bundle.id === value ? "bg-gray-100" : ""
              }`}
            >
              <div className="text-xs font-medium">{bundle.name}</div>
              <div className="text-xs text-muted-foreground">
                {detail(bundle)}
              </div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
